import { groupBy } from "ramda";

import { humanReadableFileSize } from "widgets/utils";

import type WebApiClient from "../../api/WebApiClient.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { type IUploadFileResponse } from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import { type InputSequenceUploadFileInput, type ValidationError } from "@/graphql.ts";
import {
  FILE_GLOBAL_VALIDATION_FAILED,
  FILE_UPLOADS_RESET,
  FILE_UPLOAD_ABORT,
  FILE_UPLOAD_FAILURE,
  FILE_UPLOAD_PROGRESS,
  FILE_UPLOAD_REQUEST,
  FILE_UPLOAD_SUCCESS,
  FILE_VALIDATION_COMPLETE,
  FILE_VALIDATION_FAILED,
  FILE_VALIDATION_REQUEST,
  FILE_VALIDATION_RESET,
  FILE_VALIDATION_WAITING,
} from "@/redux/action-types.ts";
import { FILE_STATUS } from "@/settings/settings.ts";

/**
 * State definition
 */
export interface IFilesUploadState {
  filesUploadLookup: Dictionary<IFileUpload>;
}

export interface IFileUploadState<T> {
  file: T;
  uploadStatus: FILE_STATUS;
  uploadStarted: number; // basically a date
  progress: number;
  errors?: string[];
  request: XMLHttpRequest; // for aborting
}

export type IFileUpload = IFileUploadState<IUploadFileResponse>;

const initialState: IFilesUploadState = {
  filesUploadLookup: {},
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [FILE_UPLOADS_RESET]: (state: IFilesUploadState): IFilesUploadState => {
    return {
      ...state,
      filesUploadLookup: {},
    };
  },

  [FILE_UPLOAD_REQUEST]: (
    state: IFilesUploadState,
    payload: { request: XMLHttpRequest; uploadData: IUploadFileResponse },
  ): IFilesUploadState => {
    // in case the same file name already exists we need to overwrite it
    if (state.filesUploadLookup[payload.uploadData.input]) {
      state.filesUploadLookup[payload.uploadData.input].request?.abort();
    }
    return updateFileDataIntoUploadStateLookup({
      state,
      filename: payload.uploadData.input,
      updatedData: {
        file: payload.uploadData,
        uploadStatus: FILE_STATUS.UPLOAD_PENDING,
        uploadStarted: Date.now(),
        progress: 0,
        request: payload.request,
      },
    });
  },

  [FILE_UPLOAD_PROGRESS]: (
    state: IFilesUploadState,
    payload: { progress: number; uploadData: IUploadFileResponse },
  ): IFilesUploadState => {
    return updateFileDataIntoUploadStateLookup({
      state,
      filename: payload.uploadData.input,
      fileShouldExist: true,
      updatedData: {
        file: payload.uploadData,
        progress: payload.progress,
      },
    });
  },

  [FILE_UPLOAD_SUCCESS]: (state: IFilesUploadState, uploadData: IUploadFileResponse): IFilesUploadState => {
    return updateFileDataIntoUploadStateLookup({
      state,
      filename: uploadData.input,
      fileShouldExist: true,
      updatedData: {
        file: uploadData,
        uploadStatus: FILE_STATUS.UPLOAD_COMPLETE,
        progress: 100,
      },
    });
  },

  [FILE_UPLOAD_FAILURE]: (
    state: IFilesUploadState,
    payload: { error: string; uploadData: IUploadFileResponse },
  ): IFilesUploadState => {
    return updateFileDataIntoUploadStateLookup({
      state,
      filename: payload.uploadData.input,
      fileShouldExist: true,
      updatedData: {
        uploadStatus: FILE_STATUS.UPLOAD_FAILED,
        progress: 0,
        errors: [payload.error],
      },
    });
  },

  [FILE_UPLOAD_ABORT]: (state: IFilesUploadState, fileName: string): IFilesUploadState => {
    const fileUploads: Dictionary<IFileUpload> = { ...state.filesUploadLookup };
    fileUploads[fileName].request.abort();
    delete fileUploads[fileName];
    return {
      ...state,
      filesUploadLookup: fileUploads,
    };
  },

  [FILE_VALIDATION_REQUEST]: (state: IFilesUploadState, fileUploads: IFileUpload[]): IFilesUploadState => {
    let uploadState: IFilesUploadState = state;
    fileUploads.forEach((fileUpload: IFileUpload) => {
      uploadState = updateFileDataIntoUploadStateLookup({
        state: uploadState,
        filename: fileUpload.file.input,
        fileShouldExist: true,
        updatedData: {
          uploadStatus: FILE_STATUS.VALIDATION_PENDING,
        },
      });
    });
    return uploadState;
  },

  [FILE_VALIDATION_COMPLETE]: (state: IFilesUploadState, fileUploads: IFileUpload[]): IFilesUploadState => {
    let uploadState: IFilesUploadState = state;
    fileUploads.forEach((fileUpload: IFileUpload) => {
      uploadState = updateFileDataIntoUploadStateLookup({
        state: uploadState,
        filename: fileUpload.file.input,
        fileShouldExist: true,
        updatedData: {
          uploadStatus: FILE_STATUS.VALIDATION_COMPLETE,
          errors: [],
        },
      });
    });
    return uploadState;
  },

  [FILE_VALIDATION_WAITING]: (state: IFilesUploadState, fileUploads: IUploadFileResponse[]): IFilesUploadState => {
    let uploadState: IFilesUploadState = state;
    fileUploads.forEach((fileUpload: IUploadFileResponse) => {
      uploadState = updateFileDataIntoUploadStateLookup({
        state: uploadState,
        filename: fileUpload.input,
        fileShouldExist: true,
        updatedData: {
          uploadStatus: FILE_STATUS.VALIDATION_WAITING,
        },
      });
    });
    return uploadState;
  },

  [FILE_VALIDATION_FAILED]: (state: IFilesUploadState, validationErrors: ValidationError[]): IFilesUploadState => {
    let uploadState: IFilesUploadState = state;
    const groupedValidationErrors = groupBy<ValidationError>(
      (validationError) => validationError.inputName!,
      validationErrors ?? [],
    );
    Object.keys(groupedValidationErrors).forEach((inputName: string) => {
      const errors: ValidationError[] = groupedValidationErrors[inputName];
      // get the original file name (since the validation error gives the file server path)
      const fileName: string = Object.keys(state.filesUploadLookup).find((fileKey: string) => {
        const uploadResponse: IUploadFileResponse = state.filesUploadLookup[fileKey].file;
        return uploadResponse && uploadResponse.uploaded === inputName;
      });
      if (fileName) {
        uploadState = updateFileDataIntoUploadStateLookup({
          state: uploadState,
          filename: fileName,
          fileShouldExist: true,
          updatedData: {
            uploadStatus: FILE_STATUS.VALIDATION_FAILED,
            errors: errors.map((error) => error.message),
          },
        });
      }
    });
    return uploadState;
  },

  [FILE_GLOBAL_VALIDATION_FAILED]: (state: IFilesUploadState): IFilesUploadState => {
    let uploadState: Dictionary<IFileUpload> = state.filesUploadLookup;
    // at least one validationError.inputName is null: that means we consider all files as failed
    // (eg due to some form-wide validation like the accumulated number of seqs is too large)
    Object.keys(state.filesUploadLookup).forEach((fileKey: string) => {
      // the message is input-scoped, not file-scoped
      uploadState = {
        ...uploadState,
        [fileKey]: {
          ...uploadState[fileKey],
          uploadStatus: FILE_STATUS.VALIDATION_FAILED,
        },
      };
    });
    return {
      ...state,
      filesUploadLookup: uploadState,
    };
  },

  [FILE_VALIDATION_RESET]: (state: IFilesUploadState): IFilesUploadState => {
    let uploadState: Dictionary<IFileUpload> = state.filesUploadLookup;
    Object.keys(state.filesUploadLookup).forEach((fileKey: string) => {
      const file = uploadState[fileKey];
      if (
        file.uploadStatus === FILE_STATUS.VALIDATION_PENDING ||
        file.uploadStatus === FILE_STATUS.VALIDATION_COMPLETE ||
        file.uploadStatus === FILE_STATUS.VALIDATION_FAILED
      ) {
        uploadState = {
          ...uploadState,
          [fileKey]: {
            ...uploadState[fileKey],
            uploadStatus: FILE_STATUS.VALIDATION_WAITING,
          },
        };
      }
    });
    return {
      ...state,
      filesUploadLookup: uploadState,
    };
  },
});

/**
 * Action creators
 */
export function resetAllFileUploads() {
  return {
    type: FILE_UPLOADS_RESET,
  };
}

export function abortFileUpload(fileName: string) {
  return {
    type: FILE_UPLOAD_ABORT,
    payload: fileName,
  };
}

export function invalidateAllFiles() {
  return {
    type: FILE_VALIDATION_RESET,
  };
}

export function uploadFile(file: File, formData: FormData) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<IUploadFileResponse> => {
    const uploadData: IUploadFileResponse = { input: file.name };
    dispatch({
      type: FILE_UPLOAD_REQUEST,
      payload: {
        uploadData: uploadData,
      },
    });

    const maxUploadSizeInBytes: number = getState().config.configuration.application.maxDiskBuffer;
    if (file.size > maxUploadSizeInBytes) {
      const { size, unit } = humanReadableFileSize(maxUploadSizeInBytes);
      dispatch({
        type: FILE_UPLOAD_FAILURE,
        payload: {
          uploadData: uploadData,
          error: `The file is too large, it should not exceed the configured size of ${size} ${unit}`,
        },
      });
      return Promise.resolve(null);
    }

    function progressCallback(evt: ProgressEvent) {
      dispatch({
        type: FILE_UPLOAD_PROGRESS,
        payload: {
          uploadData: uploadData,
          progress: Math.floor((evt.loaded * 100) / evt.total),
        },
      });
    }

    function requestStartedCallback(request: XMLHttpRequest) {
      dispatch({
        type: FILE_UPLOAD_REQUEST,
        payload: {
          uploadData: uploadData,
          request: request,
        },
      });
    }

    return webApiClient
      .uploadFile("/upload", formData, progressCallback, requestStartedCallback)
      .then(
        (response: IUploadFileResponse) => {
          dispatch({
            type: FILE_UPLOAD_SUCCESS,
            payload: response,
          });
          return response;
        },
        (error) => {
          dispatch({
            type: FILE_UPLOAD_FAILURE,
            payload: { uploadData: uploadData, error: error.message },
          });
        },
      )
      .catch((e) => {
        return createErrorHandler(dispatch, {
          actionType: FILE_UPLOAD_FAILURE,
          actionPayload: { uploadData: uploadData, error: e.message },
          alertTitle: `Upload Failed`,
          throwError: false,
        })(e);
      });
  };
}

/**
 * Utils
 */

export function allFilesHaveStatus(fileUploads: Dictionary<IFileUpload>, state: FILE_STATUS): boolean {
  const files: IFileUpload[] = Object.keys(fileUploads).map((fileUploadKey: string) => fileUploads[fileUploadKey]);
  return files.every((fileUpload: IFileUpload) => fileUpload.uploadStatus === state);
}

export function anyFileHasStatus(fileUploads: Dictionary<IFileUpload>, state: FILE_STATUS): boolean {
  const files: IFileUpload[] = Object.keys(fileUploads).map((fileUploadKey: string) => fileUploads[fileUploadKey]);
  return files.some((fileUpload: IFileUpload) => fileUpload.uploadStatus === state);
}

export function pendingUploadOrValidation(fileUploads: Dictionary<IFileUpload>): boolean {
  const files: IFileUpload[] = Object.keys(fileUploads)
    .map((fileUploadKey: string) => fileUploads[fileUploadKey])
    .filter(
      (fileUpload: IFileUpload) =>
        fileUpload.uploadStatus === FILE_STATUS.VALIDATION_PENDING ||
        fileUpload.uploadStatus === FILE_STATUS.UPLOAD_PENDING,
    );
  return files.length > 0;
}

export function getFilesToBeValidated(fileUploads: Dictionary<IFileUpload>): IFileUpload[] {
  return (
    Object.keys(fileUploads)
      .map((fileUploadKey: string) => fileUploads[fileUploadKey])
      // we always validate all the files together since we have some validation on server side taking into account the accumulated number of seqs
      .filter(
        (fileUpload: IFileUpload) =>
          fileUpload.uploadStatus !== FILE_STATUS.UPLOAD_PENDING &&
          fileUpload.uploadStatus !== FILE_STATUS.UPLOAD_FAILED,
      )
  );
}

export function getFileServerPath(fileUploads: Dictionary<IFileUpload>): InputSequenceUploadFileInput[] {
  return Object.keys(fileUploads).map((fileUploadKey: string) => {
    return {
      fileName: fileUploads[fileUploadKey].file.input,
      filePath: fileUploads[fileUploadKey].file.uploaded,
    };
  });
}

interface UpdateStateLookupProps {
  state: IFilesUploadState;
  filename: string;
  updatedData: Partial<IFileUpload>;
  fileShouldExist?: boolean;
}

function updateFileDataIntoUploadStateLookup(props: UpdateStateLookupProps): IFilesUploadState {
  const { state, filename, updatedData, fileShouldExist } = props;
  const existingFileData = state.filesUploadLookup[filename];
  if (fileShouldExist && !existingFileData) {
    // in case the file has been removed in between
    return state;
  }
  return {
    ...state,
    filesUploadLookup: {
      ...state.filesUploadLookup,
      [filename]: {
        ...existingFileData,
        ...updatedData,
      },
    },
  };
}
